import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { GitBranch, Plus } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { Mono } from '@/components/common/display';
import { NotImplementedState, LoadingRows } from '@/components/common/Feedback';
import { CreateAsnDialog } from '@/components/resource/CreateAsnDialog';
import { useStubResource, isNotImplemented } from '@/hooks/use-resource';

interface AsnItem {
  id: string;
  asn: number;
  name: string;
  rir: string;
  description: string;
}

const RIR_VARIANT: Record<string, 'default' | 'secondary' | 'info' | 'outline'> = {
  ARIN: 'info',
  RIPE: 'info',
  APNIC: 'info',
  LACNIC: 'info',
  AFRINIC: 'info',
};

const COLUMNS: ColumnDef<AsnItem>[] = [
  {
    accessorKey: 'asn',
    header: 'ASN',
    cell: ({ row }) => <Mono className="text-xs">AS{row.original.asn}</Mono>,
  },
  {
    accessorKey: 'name',
    header: 'Nombre',
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: 'rir',
    header: 'RIR',
    cell: ({ row }) =>
      row.original.rir ? (
        <Badge variant={RIR_VARIANT[row.original.rir] ?? 'outline'} className="font-mono text-[10px]">
          {row.original.rir}
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'description',
    header: 'Descripción',
    cell: ({ row }) =>
      row.original.description ? (
        <span className="text-xs text-muted-foreground">{row.original.description}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
];

export const Route = createFileRoute('/_auth/asns')({
  component: AsnsPage,
});

function AsnsPage() {
  const { data, isLoading, error } = useStubResource<AsnItem>('asns');
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="ASNs"
        icon={GitBranch}
        description="Números de sistema autónomo (Autonomous System Numbers)"
        actions={
          <Button size="sm" className="h-7 text-xs" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 size-3.5" />
            Nuevo ASN
          </Button>
        }
      />
      <CreateAsnDialog open={createOpen} onOpenChange={setCreateOpen} />

      <div className="flex min-h-0 flex-1 flex-col p-4">
        {isLoading ? (
          <div className="space-y-2 p-2">
            <LoadingRows rows={8} />
          </div>
        ) : isNotImplemented(error) ? (
          <NotImplementedState resource="ASNs" />
        ) : (
          <DataTable
            columns={COLUMNS}
            data={data?.data ?? []}
            getRowId={(r) => r.id}
            error={!isNotImplemented(error) ? error : undefined}
            emptyTitle="Sin ASNs"
            emptyDescription="No hay números de sistema autónomo registrados."
            csvFilename="asns"
          />
        )}
      </div>
    </div>
  );
}
